import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/tiptap-editor/tiptap-ui-primitive/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tiptap-editor/tiptap-ui-primitive/tooltip/tooltip';
import { useZyVersionFilterContext } from '@/components/tiptap-editor/tiptap-ui/zyversion-filter-context';
import { Checkbox } from '@/components/ui/checkbox';
import { useDeferredVersionFilter } from '@/hooks/use-deferred-version-filter';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { isNodeInSchema } from '@/lib/tiptap-utils';
import { getEditorAllowedZyVersions, getEditorVersionFilter, normalizeSelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { ChevronDown } from 'lucide-react';
import { useMemo } from 'react';

interface ZyVersionDropdownProps {
    disabled?: boolean;

    /** When omitted, uses `EditorContext`. In split view, pass the main editor (not a pane). */
    editor?: Editor | null;
}

function generateSelectedVersionsSummary(selectedVersions: string[] | null, allowedVersions: string[]): string {
    if (allowedVersions.length === 0 || selectedVersions?.length === 0) {
        return 'None';
    }
    if (selectedVersions === null) {
        return allowedVersions.join(',');
    }
    if (selectedVersions.length === allowedVersions.length && allowedVersions.every(v => selectedVersions.includes(v))) {
        return allowedVersions.join(',');
    }
    return selectedVersions.join(',');
}

export function ZyVersionDropdown({ disabled = false, editor: providedEditor }: ZyVersionDropdownProps) {
    const { setVersionFilterLoading } = useZyVersionFilterContext();
    const { editor } = useTiptapEditor(providedEditor);

    const allowedVersions =
        useEditorState({
            editor,
            selector: ({ editor: ed }) => (ed ? getEditorAllowedZyVersions(ed) : []),
        }) ?? [];

    const selectedZyVersion = useEditorState({
        editor,
        selector: ({ editor: ed }) => (ed ? getEditorVersionFilter(ed) : null),
    });

    const appliedSelection = useMemo(() => normalizeSelectedZyVersionFilter(selectedZyVersion ?? null), [selectedZyVersion]);

    const {
        displaySelection,
        popoverOpen,
        handlePopoverOpenChange,
        applyPending,
        cancelPending,
        toggleVersion,
        showAllVersions,
        clearAllVersions,
        hasPendingChanges,
        isVersionChecked,
    } = useDeferredVersionFilter({
        editor,
        appliedSelection,
        allVersions: allowedVersions,
        onLoadingChange: setVersionFilterLoading,
    });

    const selectedVersionsSummary = generateSelectedVersionsSummary(displaySelection, allowedVersions);

    if (!editor) return null;

    const isZyVersionInSchema = isNodeInSchema('zyVersionBlock', editor) && isNodeInSchema('zyVersionInline', editor);
    if (!editor || !isZyVersionInSchema || allowedVersions.length === 0) {
        return null;
    }

    return (
        <Popover
            open={disabled ? false : popoverOpen}
            onOpenChange={next => {
                if (disabled) {
                    if (popoverOpen) {
                        handlePopoverOpenChange(false);
                    }
                    return;
                }
                handlePopoverOpenChange(next);
            }}
        >
            <Tooltip
                delay={250}
                placement="top"
            >
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            disabled={disabled}
                            data-style="ghost"
                            className="h-8 w-34 min-w-0 shrink-0 justify-between gap-1 border border-gray-300 px-2 text-sm font-normal dark:border-gray-600"
                            aria-label="Filter by zyVersion"
                        >
                            <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                                <span className="shrink-0">Versions:</span>
                                <span className="min-w-0 truncate">{selectedVersionsSummary}</span>
                            </span>
                            <ChevronDown
                                className="size-4 shrink-0 opacity-70"
                                aria-hidden
                            />
                        </Button>
                    </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent portal>Versions: {selectedVersionsSummary}</TooltipContent>
            </Tooltip>
            <PopoverContent
                align="start"
                className="min-w-48 max-w-sm rounded-md border border-gray-200 bg-white p-2 shadow-md dark:border-gray-600 dark:bg-gray-800"
                onOpenAutoFocus={e => e.preventDefault()}
            >
                {allowedVersions.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No versions for this content.</p>
                ) : (
                    <>
                        <div className="mb-2 flex gap-3 border-b border-gray-200 pb-2 dark:border-gray-600">
                            <button
                                type="button"
                                className="text-left text-sm text-gray-600 hover:underline dark:text-blue-400 cursor-pointer"
                                onClick={showAllVersions}
                            >
                                Select all
                            </button>
                            <button
                                type="button"
                                className="text-left text-sm text-gray-600 hover:underline dark:text-blue-400 cursor-pointer"
                                onClick={clearAllVersions}
                            >
                                Clear all
                            </button>
                        </div>
                        <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto">
                            {allowedVersions.map(version => (
                                <li key={version}>
                                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={isVersionChecked(version)}
                                            onCheckedChange={() => toggleVersion(version)}
                                        />
                                        <span>{version}</span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-2 flex justify-end gap-2 border-t border-gray-200 pt-2 dark:border-gray-600">
                            <Button
                                type="button"
                                data-style="primary"
                                size="small"
                                disabled={!hasPendingChanges}
                                onClick={applyPending}
                            >
                                Apply
                            </Button>
                            <Button
                                type="button"
                                data-style="secondary"
                                size="small"
                                onClick={cancelPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </PopoverContent>
        </Popover>
    );
}

export default ZyVersionDropdown;
